import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { parseApiError, useAuth, usePermission } from '@saas-kit/lib';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  PageHeader,
} from '@saas-kit/ui';

import { listMembers, listTenantRoles } from './api';
import { MembersTable } from './MembersTable';
import { useMemberMutations, type MemberAction } from './useMemberMutations';
import { TenantRequiredState } from '../../components/TenantRequiredState';

export function MembersPage(): JSX.Element {
  const { t } = useTranslation('members');
  const selectedTenant = useAuth((s) => s.selectedTenant);
  const canRead = usePermission('members.read');
  const canManage = usePermission('members.manage');
  const canInvite = usePermission('members.invite');
  const [pendingAction, setPendingAction] = useState<MemberAction | null>(null);

  const membersQuery = useQuery({
    queryKey: ['memberships', selectedTenant?.tenant_id],
    queryFn: listMembers,
    enabled: Boolean(selectedTenant?.tenant_id),
  });

  const rolesQuery = useQuery({
    queryKey: ['roles', selectedTenant?.tenant_id],
    queryFn: listTenantRoles,
    enabled: Boolean(selectedTenant?.tenant_id) && canManage,
  });

  const mutations = useMemberMutations(() => setPendingAction(null));

  const members = membersQuery.data ?? [];
  const activeMembers = members.filter((member) => member.status === 'active').length;
  const suspendedMembers = members.filter((member) => member.status === 'suspended').length;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('title')}
        description={t('subtitle')}
        actions={
          <>
            {canInvite ? (
              <Link to="/team/invitations">
                <Button variant="secondary">{t('actions.open_invitations')}</Button>
              </Link>
            ) : null}
            {canManage ? (
              <Link to="/team/roles">
                <Button>{t('actions.open_roles')}</Button>
              </Link>
            ) : null}
          </>
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard label={t('metrics.total')} value={String(members.length)} />
        <MetricCard label={t('metrics.active')} value={String(activeMembers)} />
        <MetricCard label={t('metrics.suspended')} value={String(suspendedMembers)} />
      </div>

      {!selectedTenant ? (
        <TenantRequiredState
          title={t('tenant_required.title')}
          body={t('tenant_required.body')}
          cta={t('tenant_required.cta')}
        />
      ) : !canRead ? (
        <ErrorBanner>{t('errors.read_denied')}</ErrorBanner>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>{t('list.section_title')}</CardTitle>
            <CardDescription>
              {canManage ? t('list.description') : t('list.read_only')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {membersQuery.isError ? (
              <ErrorBanner>{t(parseApiError(membersQuery.error).i18nKey)}</ErrorBanner>
            ) : members.length > 0 ? (
              <MembersTable
                members={members}
                roles={rolesQuery.data ?? []}
                canManage={canManage}
                roleBusy={mutations.roleBusy}
                rolesBusy={mutations.rolesBusy}
                onChangeRole={mutations.changeRole}
                onAssignRole={mutations.assignRole}
                onRevokeRole={mutations.revokeRole}
                onAction={(kind, member) => setPendingAction({ kind, member })}
              />
            ) : membersQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">{t('loading')}</p>
            ) : (
              <EmptyState
                title={t('list.empty_title')}
                description={t('list.empty')}
                action={
                  canInvite ? (
                    <Link to="/team/invitations">
                      <Button>{t('actions.open_invitations')}</Button>
                    </Link>
                  ) : undefined
                }
              />
            )}
          </CardContent>
        </Card>
      )}

      {pendingAction ? (
        <ConfirmDialog
          title={t(`confirm.${pendingAction.kind}.title`, { name: pendingAction.member.full_name ?? pendingAction.member.email })}
          body={t(`confirm.${pendingAction.kind}.body`, { name: pendingAction.member.full_name ?? pendingAction.member.email })}
          confirmLabel={t(`actions.${pendingAction.kind}`)}
          cancelLabel={t('cancel')}
          variant={pendingAction.kind === 'remove' ? 'destructive' : 'primary'}
          isBusy={mutations.actionBusy}
          onCancel={() => setPendingAction(null)}
          onConfirm={() => mutations.runAction(pendingAction)}
        />
      ) : null}
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-2 text-2xl font-semibold text-foreground">{value}</p>
      </CardContent>
    </Card>
  );
}
